import Bugsnag from '@bugsnag/expo';
import { NoteDestination } from '../components/shared/Notes/utils/types';
import { getDefaultNoteFromNoteDestination } from '../hooks/api/useNotes';
import { APITypes } from '../primary-record-types';

/**
 * Instrumentation for the cross-profile / cross-visit note-save (misroute) bug.
 *
 * The misroute occurs when a stale create-latch note from a previous destination is still bound to a
 * reused editor. useEditNote now IGNORES such a note (so the save creates a fresh note on the CURRENT
 * destination instead of overwriting the stale one); when it does, it hands the ignored note here.
 * `isMismatch` is therefore a real signal — true exactly when a stale note was detected and ignored —
 * not a tautology.
 *
 * Output feeds two sinks:
 *  - Analytics (the `attributes` map on the existing `addNote` event), incl. `url`.
 *  - Bugsnag (a breadcrumb on every save; a notify when a misroute was prevented).
 *
 * Only internal UUIDs / enums / the in-app URL are included — never note title or body. Keys are
 * lowercase because the Glue OpenX JsonSerDe lower-cases map keys on read, so lowercase keeps
 * `attributes['...']` Athena lookups working.
 */

const str = (v: unknown): string => (v === undefined || v === null ? '' : String(v));

export type NoteSaveInstrumentation = {
    attributes: Record<string, string>;
    isMismatch: boolean;
};

export const buildNoteSaveInstrumentation = (
    noteDestination: NoteDestination,
    /** The stale note useEditNote ignored for the current destination, if any. Presence === a misroute. */
    ignoredStaleNote: APITypes.notesService.NoteDetails | undefined,
    userId: string,
    /** Which editor host the save came from (e.g. 'profile-activity.recent-notes-drawer'). */
    source: string | undefined,
): NoteSaveInstrumentation => {
    const destination = getDefaultNoteFromNoteDestination(noteDestination, userId);
    const isMismatch = !!ignoredStaleNote;

    const attributes: Record<string, string> = {
        url: typeof window !== 'undefined' ? str(window?.location?.href) : '',
        noteeditorsource: str(source),
        notesavemismatch: String(isMismatch),
        destinationentitytype: str(destination.entityType),
        destinationentityid: str(destination.entityId),
        destinationmedicalprofileid: str(destination.medicalProfileId),
        // The note we refused to overwrite (empty unless a misroute was prevented).
        ignorednoteid: str(ignoredStaleNote?.noteId),
        ignoredentitytype: str(ignoredStaleNote?.entityType),
        ignoredentityid: str(ignoredStaleNote?.entityId),
        ignoredmedicalprofileid: str(ignoredStaleNote?.medicalProfileId),
    };

    return { attributes, isMismatch };
};

/** Bugsnag sink. No-ops safely if Bugsnag isn't started (dev / localhost / expo go). */
export const reportNoteSaveToBugsnag = (
    instrumentation: NoteSaveInstrumentation,
    extra: Record<string, string | undefined>,
): void => {
    try {
        Bugsnag.leaveBreadcrumb('note-save', { ...instrumentation.attributes, ...extra });
        if (instrumentation.isMismatch) {
            Bugsnag.notify(
                new Error('Note misroute prevented (stale note ignored for current destination)'),
                (event) => {
                    event.addMetadata('noteMisroute', { ...instrumentation.attributes, ...extra });
                },
            );
        }
    } catch {
        // Bugsnag not started (dev / localhost / expo go) — instrumentation is best-effort.
    }
};
